import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"

export default function OnboardingStepper() {
  const [step, setStep] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [userRole, setUserRole] = useState<string>("")
  const navigate = useNavigate()

  // Form state
  const [formData, setFormData] = useState({
    fullName: "",
    country: "",
    age: "",
    gender: "",
    specialties: [] as string[],
    experienceYears: "",
    nclexStatus: "",
    cgfnsStatus: "",
    dataflowStatus: "",
    englishTestType: "",
    englishScore: ""
  })

  useEffect(() => {
    const fetchUserProfile = async () => {
      const user = lumi.auth.user
      if (!user) {
        navigate("/auth")
        return
      }

      try {
        const profile = await lumi.entities.UserProfiles.list({
          filter: { userId: user.userId }
        })
        if (profile.list.length > 0) {
          setUserRole(profile.list[0].role)
        }
      } catch (error) {
        console.error(error)
      }
    }
    fetchUserProfile()
  }, [navigate])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleMultiSelect = (value: string) => {
    const current = formData.specialties
    if (current.includes(value)) {
      setFormData({ ...formData, specialties: current.filter(s => s !== value) })
    } else {
      setFormData({ ...formData, specialties: [...current, value] })
    }
  }

  const handleNext = () => {
    if (step === 1) {
      if (!formData.fullName || !formData.country || !formData.age || !formData.gender) {
        toast.error("Please fill all required fields")
        return
      }
    }
    if (step === 2) {
      if (formData.specialties.length === 0 || !formData.experienceYears) {
        toast.error("Please fill all required fields")
        return
      }
    }
    setStep(step + 1)
  }

  const handleSubmit = async () => {
    setIsLoading(true)
    try {
      const user = lumi.auth.user
      if (!user) {
        toast.error("Please sign in first")
        navigate("/auth")
        return
      }

      // Update user profile with onboarding data
      const profiles = await lumi.entities.UserProfiles.list({
        filter: { userId: user.userId }
      })

      if (profiles.list.length > 0) {
        const profileId = profiles.list[0]._id
        await lumi.entities.UserProfiles.update(profileId, {
          ...formData,
          onboardingComplete: true,
          updatedAt: new Date().toISOString()
        })

        toast.success("Profile completed successfully!")
        navigate(`/dashboard/${userRole}`)
      }
    } catch (error) {
      toast.error("Failed to save profile. Please try again.")
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const specialtyOptions = [
    "Med-Surg",
    "ICU",
    "CCU",
    "NICU",
    "PICU",
    "ER/Trauma",
    "OR",
    "PACU",
    "Labor & Delivery",
    "OB/GYN",
    "Pediatrics",
    "Geriatrics",
    "Telemetry",
    "Oncology",
    "Dialysis",
    "Psychiatry",
    "Rehabilitation",
    "Home Health",
    "Long-term Care",
    "Infection Control",
    "Case Management",
    "Public Health",
    "Ambulatory Care",
    "Hospice/Palliative",
    "Other"
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 py-12 px-6">
      <div className="max-w-2xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Step {step} of 3</span>
            <span className="text-sm font-medium text-gray-600">{Math.round((step / 3) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-teal-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(step / 3) * 100}%` }}
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {step === 1 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Basic Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Country *</label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="">Select country</option>
                    <option value="Pakistan">Pakistan</option>
                    <option value="India">India</option>
                    <option value="Philippines">Philippines</option>
                    <option value="Nigeria">Nigeria</option>
                    <option value="Kenya">Kenya</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Age *</label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gender *</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="">Select gender</option>
                    <option value="Female">Female</option>
                    <option value="Male">Male</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Professional Background</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Specialties * (Select all that apply)</label>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 max-h-96 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                    {specialtyOptions.map(specialty => (
                      <button
                        key={specialty}
                        type="button"
                        onClick={() => handleMultiSelect(specialty)}
                        className={`px-3 py-2 rounded-lg border-2 transition-all text-sm ${
                          formData.specialties.includes(specialty)
                            ? "border-teal-600 bg-teal-50 text-teal-700"
                            : "border-gray-300 hover:border-teal-300"
                        }`}
                      >
                        {specialty}
                      </button>
                    ))}
                  </div>
                  {formData.specialties.length > 0 && (
                    <p className="text-sm text-teal-600 mt-2">
                      {formData.specialties.length} selected
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Years of Experience *</label>
                  <input
                    type="number"
                    name="experienceYears"
                    value={formData.experienceYears}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Certifications</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">NCLEX Status</label>
                  <select
                    name="nclexStatus"
                    value={formData.nclexStatus}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="">Select status</option>
                    <option value="Passed">Passed</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Not Started">Not Started</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">CGFNS Status</label>
                  <select
                    name="cgfnsStatus"
                    value={formData.cgfnsStatus}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="">Select status</option>
                    <option value="Passed">Passed</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Not Started">Not Started</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Dataflow Status</label>
                  <select
                    name="dataflowStatus"
                    value={formData.dataflowStatus}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="">Select status</option>
                    <option value="Complete">Complete</option>
                    <option value="Pending">Pending</option>
                    <option value="Not Started">Not Started</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">English Test Type</label>
                  <select
                    name="englishTestType"
                    value={formData.englishTestType}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="">Select test</option>
                    <option value="IELTS">IELTS</option>
                    <option value="OET">OET</option>
                    <option value="TOEFL">TOEFL</option>
                    <option value="None">None</option>
                  </select>
                </div>
                {formData.englishTestType && formData.englishTestType !== "None" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Score</label>
                    <input
                      type="text"
                      name="englishScore"
                      value={formData.englishScore}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-4 mt-8">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:border-teal-600 hover:text-teal-600 transition-colors"
              >
                Back
              </button>
            )}
            {step < 3 ? (
              <button
                onClick={handleNext}
                className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Saving..." : "Complete Profile"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
